import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { repoName } from "../../lib/bobConsole";
import FindingDetail, { type DetailProps } from "./FindingDetail";

/** Marks everything outside `el` inert, up to the body, and returns how to undo exactly that. */
function inertOutside(el: HTMLElement): () => void {
  const marked: Element[] = [];
  for (let node: HTMLElement | null = el; node && node !== document.body; node = node.parentElement) {
    for (const sibling of Array.from(node.parentElement?.children ?? [])) {
      if (sibling === node || sibling.hasAttribute("inert")) continue;
      sibling.setAttribute("inert", "");
      marked.push(sibling);
    }
  }
  return () => { for (const e of marked) e.removeAttribute("inert"); };
}

/** A finding on a phone: a modal view over the console, with a back button and the actions in a bottom bar. */
export default function FindingMobile({ onBack, ...detail }: DetailProps & { onBack: () => void }) {
  const [copied, setCopied] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const openedOn = useRef(detail.finding.id);
  const where = [repoName(detail.repo), detail.branch].filter((s) => typeof s === "string" && s !== "").join(" / ");

  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const opener = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
    const restore = inertOutside(el);
    el.querySelector<HTMLElement>(".bob-detail-title")?.focus();
    return () => {
      restore();
      const back = opener?.isConnected
        ? opener
        : document.querySelector<HTMLElement>(`li[data-finding="${openedOn.current}"] .bob-card`);
      back?.focus();
    };
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Escape") return;
    e.stopPropagation();
    onBack();
  };
  const copyLink = () => {
    void navigator.clipboard?.writeText(window.location.href).then(() => setCopied(true), () => setCopied(false));
  };
  return (
    <div ref={root} className="bob-phone" role="dialog" aria-modal="true" aria-label={`Finding #${detail.finding.id}`} onKeyDown={onKeyDown}>
      <header className="bob-phone-head">
        <button type="button" className="bob-icon-btn bob-phone-back" aria-label="Back to findings" onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
        <div className="bob-phone-title">
          <span>Finding #{detail.finding.id}</span>
          {where !== "" && <span className="bob-mono">{where}</span>}
        </div>
        <button
          type="button"
          className="bob-icon-btn"
          aria-label={copied ? "Link copied" : "Copy link to this finding"}
          onClick={copyLink}
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
            <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
          </svg>
        </button>
      </header>
      <main className="bob-phone-main">
        <FindingDetail {...detail} phone />
      </main>
    </div>
  );
}
